import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import defaultLogo from '../assets/images/kitopi.webp';
import { getAllParamsFormUrl } from '../utils/getAllParamsFormUrl';
import { getLogo } from '../utils/getLogo';
import { sites } from './sites';

interface Brand {
  siteName: string | undefined;
  desc: string | undefined;
  logo: string;
}

interface BrandContextType {
  brand: Brand;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrandContext = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrandContext must be used within an BrandContextProvider');
  }
  return context;
};

export const BrandContextProvider = ({ children }: PropsWithChildren) => {
  const params = getAllParamsFormUrl();
  const [brand, setBrand] = useState<Brand>({
    siteName: params.siteName,
    desc: undefined,
    logo: defaultLogo,
  });

  const currentSite = sites.find(site => params.siteName === site.name);

  useEffect(() => {
    const loadLogo = async () => {
      if (params.siteName) {
        const loadedLogo = await getLogo(params.siteName);
        const finalLogo = loadedLogo || defaultLogo;
        setBrand({
          siteName: params.siteName,
          desc: currentSite?.desc,
          logo: finalLogo,
        });
      } else {
        setBrand({
          siteName: params.siteName,
          desc: currentSite?.desc,
          logo: defaultLogo,
        });
      }
    };

    loadLogo();
  }, [params.siteName, currentSite?.desc]);

  return (
    <BrandContext.Provider
      value={{
        brand,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};
